import { useCallback, useRef } from 'react';
import { getActiveViewRevision } from '../view/util';
import { getTokenPath, getTokenId, resolveToken } from '../token/token';
import { useTokenContextSelector, $accessed } from '../token/context';
function areArraysShallowEqual(a, b) {
    return a.length === b.length && a.every((o, i)=>Object.is(o, b[i]));
}
/** Memoizes an array value if none of its elements change (when compared directly) */ function useShallowStableArray(value) {
    const valueRef = useRef(value);
    if (!areArraysShallowEqual(value, valueRef.current)) {
        valueRef.current = value;
    }
    return valueRef.current;
}
/**
 * Variant of `useTokenValue` that returns the value of multiple tokens in
 * a single hook call.
 *
 * @param tokens - The ordered list of all tokens. This is automatically memoized.
 * @returns The resolved values of the tokens, in the same order as `tokens`.
 * @see /apis/usetokenvalue
 */ export function useTokenValues(tokens) {
    const stableTokens = useShallowStableArray(tokens);
    const ids = stableTokens.map((token)=>getTokenId(token));
    const revision = getActiveViewRevision();
    if (revision) {
        var _revision, _$accessed;
        var _;
        (_ = (_revision = revision)[_$accessed = $accessed]) !== null && _ !== void 0 ? _ : _revision[_$accessed] = new Map();
        ids.forEach((id, i)=>{
            revision[$accessed].set(id, getTokenPath(stableTokens[i]));
        });
    }
    const selectedValueMemo = useRef(null);
    return useTokenContextSelector(useCallback((overrides, subscribe)=>{
        const selectedValue = stableTokens.map((token)=>resolveToken(token, overrides, undefined, subscribe));
        // Return the exact same array value as previous selector calls if the
        // underlying elements are the same. This prevents unnecessary forced
        // updates when the tokens change but the final values are the same.
        if (selectedValueMemo.current != null && areArraysShallowEqual(selectedValueMemo.current, selectedValue)) {
            return selectedValueMemo.current;
        }
        selectedValueMemo.current = selectedValue;
        return selectedValue;
    }, [
        stableTokens
    ]));
}

//# sourceMappingURL=useTokenValues.js.map